import {
  type CandidateResource,
  type CandidateSlotResource,
  type CompositionResource,
  type Nudge,
  type PieceKind,
} from '@studio/shared';
import { useCallback, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../shell/api.js';
import { EmptyState, Failure } from '../../shell/EmptyState.js';
import { Link, type Surface } from '../../shell/router.js';
import { useSpreadSize } from '../../shell/spread-size.js';
import { useAsync } from '../../shell/useAsync.js';
import { SlotDetails } from '../SlotDetails.js';
import { useSlotAction } from '../useSlotAction.js';
import { useExperiment } from '../useExperiment.js';
import { describeDraft, isDirty, nudge, withInstruction, EMPTY_DRAFT, type Draft } from './draft.js';
import { FeatureGraphicPreview, IconPreview } from './Preview.js';

const SINGLE_IMAGE = ['appIcon', 'featureGraphic'] as const;
type SingleImageAssetType = (typeof SINGLE_IMAGE)[number];

function isSingleImage(assetType: string): assetType is SingleImageAssetType {
  return (SINGLE_IMAGE as readonly string[]).includes(assetType);
}

const ASSET_LABEL: Record<SingleImageAssetType, string> = {
  appIcon: 'Иконка App',
  featureGraphic: 'Feature graphic',
};

function Art({
  assetType,
  artifactId,
  headline,
}: {
  assetType: SingleImageAssetType;
  artifactId: string | null;
  headline: string;
}): ReactNode {
  return assetType === 'appIcon' ? (
    <IconPreview artifactId={artifactId} headline={headline} />
  ) : (
    <FeatureGraphicPreview artifactId={artifactId} headline={headline} />
  );
}

function stateLine(slot: CandidateSlotResource): string {
  switch (slot.state) {
    case 'working':
      return 'В работе…';
    case 'ready':
      return 'Готово';
    case 'failed':
      return slot.message ?? 'Этот Direction остановился, ничего не произведя.';
    case 'needsAttention':
      return slot.message ?? 'Direction отправлен, исход неизвестен. Ждёт вас.';
    case 'cancelled':
      return 'Отменено';
  }
}

/**
 * The Direction board.
 *
 * Six complete assets arrive before anything is adjusted, and no question is asked
 * first: the current listing asset, the logo, the brand pieces and the Brief were read
 * together, and whether to keep the existing identity or leave it behind is said in the
 * Brief in ordinary words rather than chosen from a starting mode that does not exist.
 */
function DirectionBoard({ experimentId }: { experimentId: string }): ReactNode {
  const open = useExperiment(experimentId);
  const spreadSize = useSpreadSize();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const explore = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await api.spread(experimentId, 'explore');
      open.refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Ещё один набор не удалось запустить.');
    } finally {
      setBusy(false);
    }
  }, [experimentId, open]);

  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={open.refresh} />
      </section>
    );
  }
  if (open.detail === null) return <section className="page" aria-busy="true" />;

  const assetType = open.detail.experiment.assetType;
  if (!isSingleImage(assetType)) {
    return (
      <section className="page">
        <EmptyState
          title="Этот Experiment — не одиночное изображение."
          note="Доска Directions открывается для иконки App или Feature graphic. Набор скриншотов смотрится как история, preview video — как Scenes."
          action={
            <Link to={`/experiments/${experimentId}/explore`} className="primary-link">
              К Explore
            </Link>
          }
        />
      </section>
    );
  }

  const candidates = new Map(open.detail.candidates.map((candidate) => [candidate.id, candidate]));
  const rows = open.detail.batches
    .flatMap((batch) => batch.slots)
    .filter((slot) => (slot.candidateId === null ? true : candidates.get(slot.candidateId)?.hidden !== true));

  return (
    <section className="page directions">
      <div className="page-head">
        <div>
          <p className="eyebrow">{
            spreadSize === 1
              ? `${ASSET_LABEL[assetType]} · один Direction за запуск через подписку`
              : `${ASSET_LABEL[assetType]} · ${spreadSize} готовых Directions`
          }</p>
          <h1>Оценивайте там, где это живёт.</h1>
          <p className="note">
            Каждый Direction — готовый ассет, а не набор кусков. Studio вместе прочитал Brief, текущий ассет,
            логотип и всё, что вы добавили; в Brief скажите, сохранять узнаваемость или отпустить её.
          </p>
        </div>
        <button className="secondary" type="button" disabled={busy} onClick={() => void explore()}>
          {busy ? 'Запускаю…' : spreadSize === 1 ? 'Ещё один в Explore' : `Ещё ${spreadSize} в Explore`}
        </button>
      </div>

      {error === null ? null : <Failure message={error} />}

      {rows.length === 0 ? (
        <EmptyState
          title="Пока нет Directions."
          note={
            spreadSize === 1
              ? 'Для этого Experiment ещё ничего не сгенерировано. Generate запускает один Direction через подписку.'
              : `Для этого Experiment ещё ничего не сгенерировано. Generate сразу запускает ${spreadSize} готовых Directions, и все ${spreadSize} появляются здесь.`
          }
          action={
            <Link to={`/experiments/${experimentId}/brief`} className="primary-link">
              К Brief
            </Link>
          }
        />
      ) : (
        <ul className="candidate-shelf" aria-label="Directions">
          {rows.map((slot) => (
            <li key={slot.id} className={`candidate-row state-${slot.state}`}>
              <DirectionRow
                slot={slot}
                assetType={assetType}
                candidate={slot.candidateId === null ? null : (candidates.get(slot.candidateId) ?? null)}
                experimentId={experimentId}
                onChanged={open.refresh}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DirectionRow({
  slot,
  assetType,
  candidate,
  experimentId,
  onChanged,
}: {
  slot: CandidateSlotResource;
  assetType: SingleImageAssetType;
  candidate: CandidateResource | null;
  experimentId: string;
  onChanged: () => void;
}): ReactNode {
  const { busy, failure, act } = useSlotAction(onChanged);
  const headline = candidate?.headline ?? slot.headline;
  const stopped = slot.state === 'failed' || slot.state === 'needsAttention';

  return (
    <article className="slot" aria-label={headline}>
      <div className="slot-art">
        <Art assetType={assetType} artifactId={candidate?.artifactId ?? null} headline={headline} />
      </div>

      <div className="slot-body">
        <h3>{headline}</h3>
        <p className="note">{`${slot.direction.marketingAngle} · ${slot.direction.visualMetaphor} · ${slot.direction.style} · ${slot.direction.composition}`}</p>
        <p className={stopped ? 'slot-state stopped' : 'slot-state'}>{stateLine(slot)}</p>

        <div className="row-actions">
          {candidate === null ? null : (
            <>
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() =>
                  act(() => api.setCandidateFlags(candidate.id, { keep: !candidate.keep }), 'Этот Candidate не удалось Keep.')
                }
              >
                {candidate.keep ? `Kept ${headline}` : `Keep ${headline}`}
              </button>
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() =>
                  act(() => api.setCandidateFlags(candidate.id, { hidden: true }), 'Этот Candidate не удалось скрыть.')
                }
              >
                {`Скрыть ${headline}`}
              </button>
              <Link to={`/experiments/${experimentId}/refine/${candidate.id}/bench`} className="secondary-link">
                {`Править ${headline}`}
              </Link>
            </>
          )}
          {!stopped ? null : (
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() =>
                act(() => api.retrySlot(slot.id), 'Этот слот не удалось повторить.')
              }
            >
              {`Повторить ${headline}`}
            </button>
          )}
        </div>
        {failure === null ? null : <Failure message={failure} />}

        {slot.state === 'working' && candidate === null ? null : <SlotDetails slotId={slot.id} />}
      </div>
    </article>
  );
}

const MOVES: readonly { readonly nudge: Nudge; readonly direction: string }[] = [
  { nudge: 'up', direction: 'вверх' },
  { nudge: 'down', direction: 'вниз' },
  { nudge: 'left', direction: 'влево' },
  { nudge: 'right', direction: 'вправо' },
];

const SCALE_LABEL: Record<'smaller' | 'larger', string> = {
  smaller: 'меньше',
  larger: 'больше',
};

const SCALES: readonly ('smaller' | 'larger')[] = ['smaller', 'larger'];

/**
 * The Composition bench.
 *
 * Only the pieces the Studio inferred are here, and they are moved rather than edited:
 * no layer tree, no mask, no blend mode, no coordinate panel and no prompt. Everything
 * done here is a local draft until `Create edited Candidate`, and that press creates a
 * descendant and leaves the parent exactly as it was.
 */
function CompositionBench({ experimentId, candidateId }: { experimentId: string; candidateId: string }): ReactNode {
  const composition = useAsync<CompositionResource>(() => api.readComposition(candidateId), [candidateId]);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [selected, setSelected] = useState<PieceKind | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const create = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await api.createEditedCandidate(candidateId, {
        adjustments: draft.adjustments.map((adjustment) => ({ piece: adjustment.piece, nudges: [...adjustment.nudges] })),
        ...(draft.instruction.trim() === '' ? {} : { instruction: draft.instruction.trim() }),
      });
      setDraft(EMPTY_DRAFT);
      setSent('Отредактированный Candidate уже в пути. Этот не тронут и остаётся на доске.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Отредактированный Candidate не удалось запустить.');
    } finally {
      setBusy(false);
    }
  }, [candidateId, draft]);

  if (composition.error !== null) {
    return (
      <section className="page">
        <Failure message={composition.error} onRetry={composition.reload} />
      </section>
    );
  }
  const open = composition.value;
  if (open === null) return <section className="page" aria-busy="true" />;
  if (!isSingleImage(open.assetType)) {
    return (
      <section className="page">
        <EmptyState
          title="Для этого Candidate нет bench."
          note="Composition bench открывается для иконки App или Feature graphic."
          action={
            <Link to={`/experiments/${experimentId}/explore`} className="primary-link">
              К Explore
            </Link>
          }
        />
      </section>
    );
  }

  const focus = selected ?? open.pieces[0]?.kind ?? null;
  const said = describeDraft(draft);

  return (
    <section className="page bench">
      <div className="page-head">
        <div>
          <p className="eyebrow">{`${ASSET_LABEL[open.assetType]} · ${open.headline}`}</p>
          <h1>Двигайте только то, что важно.</h1>
          <p className="note">
            Вот из каких частей оказался собран этот Direction. Сдвиньте одну или скажите изменение обычными словами.
            Ничего не сохраняется, пока вы не создадите отредактированный Candidate.
          </p>
        </div>
        <Link to={`/experiments/${experimentId}/explore/directions`} className="secondary-link">
          Все Directions
        </Link>
      </div>

      {error === null ? null : <Failure message={error} />}
      {sent === null ? null : <p className="note">{sent}</p>}

      <div className="bench-layout">
        <div className="bench-art">
          <Art assetType={open.assetType} artifactId={open.artifactId} headline={open.headline} />
        </div>

        {/*
          * Every organ of the bench, in one named group.
          *
          * The name is what a test can hold on to: what must not exist here - a layer
          * tree, a blend mode, an opacity or a coordinate field - is absent from these
          * controls, and that is a fact about the bench rather than about the words the
          * spread happened to choose. A creative direction beside them may legitimately
          * be called `overlapping layers`; a control may not.
          */}
        <aside className="bench-pieces" role="group" aria-label="Управление Composition bench">
          <h2>Части этого ассета</h2>
          <ul aria-label="Части этого ассета">
            {open.pieces.map((piece) => (
              <li key={piece.kind}>
                <button
                  type="button"
                  className="ghost"
                  aria-pressed={focus === piece.kind}
                  onClick={() => setSelected(piece.kind)}
                >
                  {`Выбрать ${piece.label}`}
                </button>
                <span className="note">{piece.note}</span>
              </li>
            ))}
          </ul>

          {focus === null ? null : (
            <div className="bench-nudges" role="group" aria-label={`Править ${labelOf(open, focus)}`}>
              {MOVES.map((move) => (
                <button
                  key={move.nudge}
                  type="button"
                  className="ghost"
                  onClick={() => setDraft((current) => nudge(current, focus, move.nudge))}
                >
                  {`Сдвинуть ${labelOf(open, focus)} ${move.direction}`}
                </button>
              ))}
              {SCALES.map((scale) => (
                <button
                  key={scale}
                  type="button"
                  className="ghost"
                  onClick={() => setDraft((current) => nudge(current, focus, scale))}
                >
                  {`Сделать ${labelOf(open, focus)} ${SCALE_LABEL[scale]}`}
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>

      <div className="bench-draft">
        <label htmlFor="bench-instruction">Опишите изменение</label>
        <input
          id="bench-instruction"
          value={draft.instruction}
          placeholder="Дайте знаку воздух, сохраните цвет"
          onChange={(event) => setDraft((current) => withInstruction(current, event.target.value))}
        />
        <p className="note" role="status">
          {said === ''
            ? 'Пока ничего не изменено. Сдвиг части меняет только этот черновик.'
            : `Черновик, не сохранён: ${said}. Candidate на доске не меняется, пока вы не создадите отредактированный Candidate.`}
        </p>
        <div className="row-actions">
          <button type="button" className="primary" disabled={busy || !isDirty(draft)} onClick={() => void create()}>
            Create edited Candidate
          </button>
          <button type="button" className="ghost" disabled={!isDirty(draft)} onClick={() => setDraft(EMPTY_DRAFT)}>
            Сбросить черновик
          </button>
        </div>
      </div>
    </section>
  );
}

function labelOf(composition: CompositionResource, kind: PieceKind): string {
  return composition.pieces.find((piece) => piece.kind === kind)?.label ?? kind;
}

export const surfaces: readonly Surface[] = [
  {
    id: 'experiment-directions',
    path: '/experiments/:experimentId/explore/directions',
    title: 'Directions',
    render: (params) => <DirectionBoard experimentId={params.experimentId ?? ''} />,
  },
  {
    id: 'experiment-composition-bench',
    path: '/experiments/:experimentId/refine/:candidateId/bench',
    title: 'Composition bench',
    render: (params) => (
      <CompositionBench experimentId={params.experimentId ?? ''} candidateId={params.candidateId ?? ''} />
    ),
  },
];
